import { type SyntheticEvent, useCallback } from "react";
import { type BuildAndBuyDispatchType, useBnbSelector } from "../../../redux/store";
import type { CtaButtonType } from "../../../../../../common-deprecated/server/data/FormatBnbCtas";
import { setStep } from "../../../redux/actions/NavigationActions";
import { NavStep } from "../../../../shared-logic/utils/Navigation";
import { trackCtaCheckout, trackCtaCustom, trackCtaMain, trackCtaSaveAccount } from "../../../utils/tracking";
import Debug from "../../../../../../common-deprecated/Debug";
import {
    getAemFormsUrl,
    getCtaArguments,
    handleCustomCta,
    handleUrl,
} from "../../../../../shared-logic/utils/ctaButtonUtils";
import { postalPublish } from "../../../../../../common-deprecated/utils/postalUtils";
import {
    POSTAL_CHANNEL_T1_CARCONFIG,
    POSTAL_CHANNEL_T1_DEALER,
    POSTAL_TOPIC_CARCONFIG_SAVE_CONFIG,
    POSTAL_TOPIC_DEALERFINDER_OPEN_IN_OVERLAY,
} from "../../../../../../common-deprecated/constants";
import { useDispatch } from "../../../../../../shared-logic/redux/store";

export type UseCtaButtonType = {
    onClick: (event: SyntheticEvent) => void;
    target: string;
    href: string;
    title: string;
};

// This hook returns the needed props of a CTA button configuration for a link tag.
// Separate hook because it needs client side T1 code.
const useCtaButton = (ctaConfig: CtaButtonType): UseCtaButtonType => {
    const dispatch = useDispatch<BuildAndBuyDispatchType>();
    const modelInfo = useBnbSelector((state) => state.car.model);
    const saveData = useBnbSelector((state) => state.save.data);
    const step = useBnbSelector((state) => state.navigation.step);
    const { country, language, brand } = useBnbSelector((state) => state.commonSettings);

    const { tyCode, href, target } = getCtaArguments(saveData, modelInfo, ctaConfig);

    const onClick = useCallback(
        (event: SyntheticEvent): void => {
            event.preventDefault();
            event.stopPropagation();

            switch (ctaConfig.type) {
                case "go-to-buy": {
                    trackCtaCheckout(step, "online-retailing-checkout");
                    dispatch(setStep(NavStep.Buy));
                    break;
                }
                case "go-to-reserve": {
                    trackCtaCheckout(step, "online-retailing-checkout-reservation");
                    dispatch(setStep(NavStep.Reserve));
                    break;
                }
                case "go-to-lease": {
                    trackCtaCheckout(step, "online-retailing-checkout-leasing");
                    dispatch(setStep(NavStep.Lease));
                    break;
                }
                case "dealerfinder": {
                    trackCtaMain(step, "dealerfinder");
                    postalPublish({
                        channel: POSTAL_CHANNEL_T1_DEALER,
                        topic: POSTAL_TOPIC_DEALERFINDER_OPEN_IN_OVERLAY,
                    });
                    break;
                }
                case "save": {
                    trackCtaSaveAccount();
                    postalPublish({
                        channel: POSTAL_CHANNEL_T1_CARCONFIG,
                        topic: POSTAL_TOPIC_CARCONFIG_SAVE_CONFIG,
                        data: { configCode: tyCode },
                    });
                    break;
                }
                case "download": {
                    trackCtaMain(step, "online-brochure");
                    const printUrl = `/bin/api/dxp/pdf?tyCode=${tyCode}&country=${country}&language=${language}&brand=${brand}`;
                    window.open(printUrl);
                    break;
                }
                case "sendtodealer": {
                    trackCtaMain(step, "send-to-dealer");
                    // fallback to url for backwards compat (from before this settings.forms field existed)
                    const contactDealerLink = getAemFormsUrl("dealer", href || "/forms/contact-dealer.html", tyCode);
                    handleUrl(contactDealerLink!, target);
                    break;
                }
                case "testdrive": {
                    trackCtaMain(step, "test-drive");
                    const testDriveFormLink = getAemFormsUrl("Test drive", href, tyCode);
                    if (testDriveFormLink) {
                        handleUrl(testDriveFormLink, target);
                    } else {
                        Debug.error("No testdrive CTA url defined");
                    }
                    break;
                }
                case "ebrochurepersonalized": {
                    trackCtaMain(step, "personalized-brochure");
                    const brochureFormLink = getAemFormsUrl("brochure", href, tyCode);
                    if (brochureFormLink) {
                        handleUrl(brochureFormLink, target);
                    } else {
                        Debug.error("No brochure CTA url defined");
                    }
                    break;
                }
                case "custom": {
                    trackCtaCustom(step, ctaConfig.tracking || ctaConfig.title);
                    handleCustomCta(ctaConfig, href, tyCode, target);
                }
            }
        },
        [
            dispatch,
            ctaConfig.type,
            ctaConfig.tracking,
            ctaConfig.jsfunction,
            href,
            target,
            step,
            tyCode,
            country,
            language,
        ],
    );

    return {
        onClick,
        href,
        target,
        title: ctaConfig.title,
    };
};

export default useCtaButton;
